import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { CopyButton } from '@/shared/components/common'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'

import { revealErrorMessage, useRevealSecret } from '../api'

/** Cuánto hay que mantener presionado antes de disparar el revelado. */
export const HOLD_MS = 600
/** El secreto se borra solo a los 30 s (plan 3.5). */
export const CLEAR_MS = 30_000

export interface SecretRevealProps {
  accessId: string
  /** Para el `aria-label`: "Revelar la contraseña de Panel de Shopify". */
  label: string
  /** `can(me, 'access:reveal')`. En false NO se renderiza el botón. */
  canReveal: boolean
  className?: string
}

/**
 * El secreto, detrás de un "mantener presionado".
 *
 * Tres reglas que no se negocian y que están testeadas:
 *
 * 1. **Antes de revelar, el secreto no está en el DOM.** No se renderiza con
 *    `type="password"` ni oculto por CSS: directamente no existe hasta que
 *    alguien lo pide.
 * 2. **Nunca entra a la cache de React Query.** Se pide con `useMutation` y,
 *    apenas llega, se hace `reset()` para que tampoco quede en el mutation
 *    cache. Vive en este `useState` y en ningún otro lado.
 * 3. **Se limpia a los 30 s y al desmontar.** Un secreto en pantalla en una
 *    llamada compartida es una fuga.
 *
 * El gesto es deliberadamente incómodo: revelar escribe una fila de auditoría
 * y consume cupo del rate-limit. Un click accidental no debería hacer eso.
 */
export function SecretReveal({ accessId, label, canReveal, className }: SecretRevealProps) {
  const t = useT()
  const reveal = useRevealSecret(accessId)
  const [secret, setSecret] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [holding, setHolding] = useState(false)

  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearHoldTimer = useCallback(() => {
    if (holdTimer.current) clearTimeout(holdTimer.current)
    holdTimer.current = null
  }, [])

  const hide = useCallback(() => {
    if (clearTimer.current) clearTimeout(clearTimer.current)
    clearTimer.current = null
    setSecret(null)
  }, [])

  // Al desmontar: ni el secreto ni los timers sobreviven a la pantalla.
  useEffect(
    () => () => {
      if (holdTimer.current) clearTimeout(holdTimer.current)
      if (clearTimer.current) clearTimeout(clearTimer.current)
    },
    [],
  )

  const { mutateAsync, reset } = reveal

  const run = useCallback(async () => {
    setError(null)
    try {
      const result = await mutateAsync()
      setSecret(result.secret)
      if (clearTimer.current) clearTimeout(clearTimer.current)
      clearTimer.current = setTimeout(() => setSecret(null), CLEAR_MS)
    } catch (caught) {
      setError(revealErrorMessage(caught, t))
    } finally {
      // El secreto no se queda tampoco en el estado de la mutation.
      reset()
    }
  }, [mutateAsync, reset, t])

  const startHold = useCallback(() => {
    if (secret || reveal.isPending) return
    setHolding(true)
    clearHoldTimer()
    holdTimer.current = setTimeout(() => {
      setHolding(false)
      void run()
    }, HOLD_MS)
  }, [clearHoldTimer, reveal.isPending, run, secret])

  const cancelHold = useCallback(() => {
    setHolding(false)
    clearHoldTimer()
  }, [clearHoldTimer])

  if (!canReveal) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-2xs text-text-3', className)}>
        <EyeOff size={12} aria-hidden="true" />
        {t('accesses.secret.noPermission')}
      </span>
    )
  }

  if (secret) {
    return (
      <span className={cn('inline-flex min-w-0 items-center gap-1.5', className)}>
        <code className="num truncate rounded-[var(--radius-control)] bg-surface-2 px-1.5 py-0.5 text-xs text-text-1">
          {secret}
        </code>
        <CopyButton value={secret} label={t('accesses.secret.copyLabel', { acceso: label })} />
        <button
          type="button"
          onClick={hide}
          className="text-2xs text-text-3 underline hover:text-text-1"
        >
          {t('accesses.secret.hide')}
        </button>
        <span className="num text-2xs text-text-3">
          {t('accesses.secret.autoHide', { count: CLEAR_MS / 1000 })}
        </span>
      </span>
    )
  }

  return (
    <span className={cn('inline-flex min-w-0 flex-wrap items-center gap-1.5', className)}>
      <button
        type="button"
        aria-label={t('accesses.secret.revealLabel', { acceso: label })}
        disabled={reveal.isPending}
        onPointerDown={startHold}
        onPointerUp={cancelHold}
        onPointerLeave={cancelHold}
        onPointerCancel={cancelHold}
        onKeyDown={(event) => {
          if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) {
            event.preventDefault()
            startHold()
          }
        }}
        onKeyUp={(event) => {
          if (event.key === ' ' || event.key === 'Enter') cancelHold()
        }}
        onBlur={cancelHold}
        className={cn(
          'hairline inline-flex h-6 items-center gap-1.5 rounded-[var(--radius-control)] px-2',
          'text-2xs text-text-2 select-none',
          'transition-colors duration-[var(--motion-fast)] hover:text-text-1',
          holding && 'bg-surface-2 text-text-1',
          'disabled:opacity-50',
        )}
      >
        {reveal.isPending ? (
          <Loader2 size={12} aria-hidden="true" className="animate-spin" />
        ) : (
          <Eye size={12} aria-hidden="true" />
        )}
        {holding ? t('accesses.secret.holding') : t('accesses.secret.hold')}
      </button>

      {error ? (
        <span role="alert" className="text-2xs" style={{ color: 'hsl(var(--overdue))' }}>
          {error}
        </span>
      ) : null}
    </span>
  )
}
